import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from './Pagination';

const ReportedUsersDisplay = ({ statusFilter = '', searchQuery = '', onUserClick }) => {
  const [reportedUsers, setReportedUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchReportedUsers = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
      };

      // Add status filter if provided
      if (statusFilter) {
        params.status = statusFilter;
      }

      // Add search query if provided
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await axios.get(`${API_BASE_URL}/admin/reported-users`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.data.data && response.data.pagination) {
        setReportedUsers(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old API format
        setReportedUsers(Array.isArray(response.data) ? response.data : []);
        setPagination(null);
      }
    } catch (err) {
      console.error('Failed to fetch reported users:', err);
      setError('Failed to fetch reported users');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchReportedUsers(page);
  };

  // Reset to page 1 when status filter or search changes
  useEffect(() => {
    setCurrentPage(1);
    fetchReportedUsers(1);
  }, [statusFilter, searchQuery]);

  // Status badge helper function
  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { bg: '#ffc107', color: '#000', text: 'Pending' },
      'DISMISSED': { bg: '#6c757d', color: '#fff', text: 'Dismissed' },
      'RESOLVED': { bg: '#28a745', color: '#fff', text: 'Resolved' },
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    return (
      <span style={{ 
        backgroundColor: config.bg, 
        color: config.color, 
        padding: '4px 10px', 
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }}>
        {config.text}
      </span>
    );
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div>
      <table
        cellPadding="8"
        cellSpacing="0"
        style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}
      >
        <thead style={{
          backgroundColor: 'rgb(250, 248, 255)',
          borderTop: '1px solid rgb(233, 230, 241)',
          borderBottom: '1px solid rgb(233, 230, 241)',
        }}>
          <tr>
            <th className="p-3 text-center">User</th>
            <th className="p-3 text-center">Email</th>
            <th className="p-3 text-center">University</th>
            <th className="p-3 text-center">Reports</th>
            <th className="p-3 text-center">Status</th>
            <th className="p-3 text-center">First Reported</th>
            <th className="p-3 text-center">Last Reported</th>
            <th className="p-3 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {reportedUsers.length === 0 ? (
            <tr>
              <td colSpan="8" className="p-2 text-center">
                No reported users found
              </td>
            </tr>
          ) : (
            reportedUsers.map((item) => {
              return (
                <tr className='border-bottom' key={item.userId}>
                  <td style={{ padding: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {item.user?.selfieUrl ? (
                      <img
                        src={item.user.selfieUrl}
                        alt={item.user?.fullName}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: '1px solid #ddd',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          background: '#ccc',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#666',
                          fontWeight: 'bold'
                        }}
                      >
                        {item.user?.fullName?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{item.user?.fullName || 'N/A'}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {item.user?.isVerified ? '✓ Verified' : 'Not Verified'}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-center">{item.user?.email || 'N/A'}</td>
                  <td className="p-2 text-center">{item.user?.university || 'N/A'}</td>
                  <td className="p-2 text-center">
                    <span style={{ 
                      backgroundColor: '#ff4444', 
                      color: 'white', 
                      padding: '4px 8px', 
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {item.totalReports}
                    </span>
                  </td>
                  <td className="p-2 text-center">
                    {getStatusBadge(item.overallStatus || 'PENDING')}
                  </td>
                  <td className="p-2 text-center">
                    {new Date(item.firstReportedAt).toLocaleDateString()}
                  </td>
                  <td className="p-2 text-center">
                    {new Date(item.lastReportedAt).toLocaleDateString()}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => onUserClick && onUserClick(item)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {pagination && (
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default ReportedUsersDisplay;
